import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from '@components/atoms/Form';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { Space } from '@components/atoms/Space';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Typography } from '@components/atoms/Typography';
import { Select, SelectType } from '@components/atoms/Select';
import { Button, ButtonMode } from '@components/atoms/Button';
import { SITE_URLS } from '@constants';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useData } from '@hooks/useData';
import { useNotifications } from '@hooks/useNotifications';
import { useUpdateCoupon } from '@services/useUpdateCoupon';
import { useGetAllMyAccountStores } from '@services/useGetAllMyAccountStores';
import { InputErrors } from '@types';
import { useCreateCoupon } from '@services/useCreateCoupon';

interface CouponFormErrors {
    general: string;
    promoCode: InputErrors;
    startDate: InputErrors;
    endDate: InputErrors;
    discountType: InputErrors;
    discountValue: InputErrors;
    totalLimit: InputErrors;
    isCumulative: InputErrors;
    state: InputErrors;
    store: InputErrors;
}

interface CouponFormProps {
    edit: boolean;
}

export const CouponForm: FC<CouponFormProps> = ({ edit }) => {
    const { navigate } = useCustomNavigate();
    const { showDefaultNotification } = useNotifications();
    const { login, myCouponDetail, isLoadingMyCouponDetail, createdCoupon, allMyAccountStores, isLoadingAllMyAccountStores } =
        useData();
    const {
        request: requestCreateCoupon,
        errorMessage: errorMessageCreate,
        validationErrors: validationErrorsCreate,
        success: successMessageCreate,
        lastResponseTime: lastCreate,
    } = useCreateCoupon();
    const {
        request: requestUpdateCoupon,
        lastResponseTime: lastEdit,
        errorMessage: errorMessageEdit,
        validationErrors: validationErrorsEdit,
        success: successMessageEdit,
    } = useUpdateCoupon();
    const { request: requestAllMyAccountStores } = useGetAllMyAccountStores();
    const { t } = useTranslation();

    const translations = {
        promoCodeLabel: t('my-coupon-form.promo-code.label'),
        storeLabel: t('my-coupon-form.store.label'),
        stateLabel: t('my-coupon-form.state.label'),
        startDateLabel: t('my-coupon-form.start-date.label'),
        endDateLabel: t('my-coupon-form.end-date.label'),
        discountTypeLabel: t('my-coupon-form.discount-type.label'),
        discountValueLabel: t('my-coupon-form.discount-value.label'),
        totalLimitLabel: t('my-coupon-form.total-limit.label'),
        isCumulativeLabel: t('my-coupon-form.is-cumulative.label'),
        isCumulativeYes: t('my-coupon-form.is-cumulative.yes'),
        isCumulativeNo: t('my-coupon-form.is-cumulative.no'),
        promoCodeRequiredError: t('my-coupon-form.promo-code.required-error'),
        storeRequiredError: t('my-coupon-form.store.required-error'),
        stateRequiredError: t('my-coupon-form.state.required-error'),
        startDateRequiredError: t('my-coupon-form.start-date.required-error'),
        endDateRequiredError: t('my-coupon-form.end-date.required-error'),
        discountTypeRequiredError: t('my-coupon-form.discount-type.required-error'),
        discountValueRequiredError: t('my-coupon-form.discount-value.required-error'),
        totalLimitRequiredError: t('my-coupon-form.total-limit.required-error'),
        isCumulativeRequiredError: t('my-coupon-form.is-cumulative.required-error'),
        submitButton: t('my-coupon-form.submit.button'),
        default: t('select-default-option'),
        couponStateActive: t('coupon-state.active'),
        couponStateInactive: t('coupon-state.inactive'),
        couponStateExpired: t('coupon-state.expired'),
        discountTypeFixed: t('discount-type.fixed'),
        discountTypePercentage: t('discount-type.percentage'),
    };

    // call services, if we don't have the information yet
    useEffect(() => {
        !allMyAccountStores && !isLoadingAllMyAccountStores && requestAllMyAccountStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allMyAccountStores]);

    const formRef = useRef<HTMLFormElement>(null);
    const [promoCode, setPromoCode] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [discountType, setDiscountType] = useState<string | null>(null);
    const [discountValue, setDiscountValue] = useState<number>(0);
    const [totalLimit, setTotalLimit] = useState<number>(0);
    const [isCumulative, setIsCumulative] = useState<string>('');
    const [state, setState] = useState<string | null>(null);
    const [store, setStore] = useState<string | null>(null);

    const stores = (allMyAccountStores?.data || []).map((item) => {
        return { value: item.uuid || '', text: item.name || '' };
    });

    // initially, update fields
    useEffect(() => {
        if (edit) {
            setPromoCode(myCouponDetail?.data?.promo_code || '');
            setStartDate(myCouponDetail?.data?.start_date || '');
            setEndDate(myCouponDetail?.data?.end_date || '');
            setDiscountType(myCouponDetail?.data?.discount_type || '');
            setDiscountValue(myCouponDetail?.data?.discount_value || 0);
            setTotalLimit(myCouponDetail?.data?.total_usage_limit || 0);
            setIsCumulative(myCouponDetail?.data?.is_cumulative || '');
            setState(myCouponDetail?.data?.status || null);
            setStore(myCouponDetail?.data?.store?.uuid || '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myCouponDetail]);

    // errors to be displayed in the form
    const [errors, setErrors] = useState<CouponFormErrors>({
        general: '',
        promoCode: [],
        startDate: [],
        endDate: [],
        discountType: [],
        discountValue: [],
        totalLimit: [],
        isCumulative: [],
        state: [],
        store: [],
    });

    const handleOnClickSaveCoupon = () => {
        const errors = validateForm();

        if (
            errors.promoCode.length === 0 &&
            errors.startDate.length === 0 &&
            errors.endDate.length === 0 &&
            errors.discountType.length === 0 &&
            errors.discountValue.length === 0 &&
            errors.totalLimit.length === 0 &&
            errors.state.length === 0 &&
            errors.store.length === 0
        ) {
            formRef?.current?.requestSubmit();
        }
    };

    const validateForm: () => CouponFormErrors = () => {
        const newErrors: CouponFormErrors = {
            general: '',
            promoCode: [],
            startDate: [],
            endDate: [],
            discountType: [],
            discountValue: [],
            totalLimit: [],
            isCumulative: [],
            state: [],
            store: [],
        };

        !promoCode.trim() && newErrors.promoCode.push(translations.promoCodeRequiredError);
        !startDate.trim() && newErrors.startDate.push(translations.startDateRequiredError);
        !endDate.trim() && newErrors.endDate.push(translations.endDateRequiredError);
        !discountType && newErrors.discountType.push(translations.discountTypeRequiredError);
        !discountValue && newErrors.discountValue.push(translations.discountValueRequiredError);
        (!totalLimit || totalLimit <= 0) && newErrors.totalLimit.push(translations.totalLimitRequiredError);
        !state && newErrors.state.push(translations.stateRequiredError);
        !isCumulative.trim() && newErrors.isCumulative.push(translations.isCumulativeRequiredError);
        // store is required only in create form
        if (!edit) {
            !store && newErrors.store.push(translations.storeRequiredError);
        }

        setErrors(newErrors);

        return newErrors;
    };

    const submitForm: () => void = async () => {
        const data = {
            promo_code: promoCode,
            start_date: startDate,
            end_date: endDate,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            discount_type: (discountType || '') as any,
            discount_value: discountValue,
            total_usage_limit: totalLimit,
            is_cumulative: isCumulative ? (Number(isCumulative) as 0 | 1) : undefined,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            status: (state || '') as any,
        };

        if (edit === true) {
            requestUpdateCoupon({ uuid: myCouponDetail?.data?.uuid || '' }, data);
        } else {
            requestCreateCoupon({ ...data, store_uuid: store || '' });
        }
    };

    // if create service returned successfully, dispatch notification and go to coupons's list page
    // if not, show errors
    useEffect(() => {
        if (lastCreate) {
            if (errorMessageCreate || validationErrorsCreate) {
                setErrors({
                    general: errorMessageCreate as string,
                    // match form request errors with frontend errors object
                    promoCode: validationErrorsCreate?.promo_code || [],
                    startDate: validationErrorsCreate?.start_date || [],
                    endDate: validationErrorsCreate?.end_date || [],
                    discountType: validationErrorsCreate?.discount_type || [],
                    discountValue: validationErrorsCreate?.discount_value || [],
                    totalLimit: validationErrorsCreate?.total_usage_limit || [],
                    isCumulative: validationErrorsCreate?.is_cumulative || [],
                    state: validationErrorsCreate?.status || [],
                    store: validationErrorsCreate?.store_id || [],
                });
            } else if (login && createdCoupon !== null) {
                showDefaultNotification({ type: 'success', message: successMessageCreate || '' });
                navigate(`/${SITE_URLS.myCoupons}`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastCreate, createdCoupon]);

    // if edit service returned successfully, dispatch notification and update
    // if not, show error
    useEffect(() => {
        if (lastEdit) {
            if (errorMessageEdit || validationErrorsEdit) {
                setErrors({
                    general: errorMessageEdit as string,
                    // match form request errors with frontend errors object
                    promoCode: validationErrorsEdit?.promo_code || [],
                    startDate: validationErrorsEdit?.start_date || [],
                    endDate: validationErrorsEdit?.end_date || [],
                    discountType: validationErrorsEdit?.discount_type || [],
                    discountValue: validationErrorsEdit?.discount_value || [],
                    totalLimit: validationErrorsEdit?.total_usage_limit || [],
                    isCumulative: validationErrorsEdit?.is_cumulative || [],
                    state: validationErrorsEdit?.status || [],
                    store: validationErrorsEdit?.store_id || [],
                });
            } else if (login) {
                showDefaultNotification({ type: 'success', message: successMessageEdit || '' });
                navigate(`/${SITE_URLS.myCoupons}`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastEdit]);

    const wasUsed: boolean = myCouponDetail?.data?.used ? myCouponDetail.data.used > 0 : false;

    return (
        <Form formRef={formRef} onSubmit={submitForm}>
            <>
                {errors.general && (
                    <>
                        <ErrorMessages messages={[errors.general]} />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </>
                )}
            </>

            <div className="row">
                {/* promo code */}
                <div className="col-md-6 col-12 pt-md-2 mt-md-1">
                    <Input
                        type={InputTypes.text}
                        variant={InputVariants.labeledInput}
                        label={`${translations.promoCodeLabel}*`}
                        value={promoCode}
                        onChange={(value) => setPromoCode(value)}
                        disabled={isLoadingMyCouponDetail || wasUsed}
                        state={errors.promoCode.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.promoCode}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
                {/* store */}
                <div className="col-md-6 col-12">
                    <div>
                        <Typography color="muted" fontSize="12">
                            <>{translations.storeLabel}*</>
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            disabled={
                                edit ||
                                isLoadingMyCouponDetail ||
                                allMyAccountStores === null ||
                                isLoadingAllMyAccountStores ||
                                wasUsed
                            }
                            options={[{ value: '', text: translations.default }, ...stores]}
                            value={store || undefined}
                            onChange={(value) => setStore(value)}
                        />
                        {errors.store.length > 0 && <ErrorMessages messages={errors.store} />}
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
                {/* state */}
                <div className="col-md-6 col-12">
                    <div>
                        <Typography color="muted" fontSize="12">
                            <>{translations.stateLabel}*</>
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            disabled={isLoadingMyCouponDetail}
                            options={[
                                { value: '', text: translations.default },
                                { value: 'active', text: translations.couponStateActive },
                                { value: 'inactive', text: translations.couponStateInactive },
                                { value: 'expired', text: translations.couponStateExpired },
                            ]}
                            value={state || undefined}
                            onChange={(value) => setState(value)}
                        />
                        {errors.state.length > 0 && <ErrorMessages messages={errors.state} />}
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
                {/* is cumulative */}
                <div className="col-md-6 col-12">
                    <div>
                        <Typography color="muted" fontSize="12">
                            <>{translations.isCumulativeLabel}*</>
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            disabled={isLoadingMyCouponDetail || wasUsed}
                            options={[
                                { value: '', text: translations.default },
                                { value: '1', text: translations.isCumulativeYes },
                                { value: '0', text: translations.isCumulativeNo },
                            ]}
                            value={`${isCumulative}` || undefined}
                            onChange={(value) => setIsCumulative(value)}
                        />
                        {errors.isCumulative.length > 0 && <ErrorMessages messages={errors.isCumulative} />}
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
                {/* start date */}
                <div className="col-md-6 col-12">
                    <Input
                        type={InputTypes.date}
                        label={`${translations.startDateLabel}*`}
                        variant={InputVariants.labeledInput}
                        value={startDate}
                        onChange={(value) => setStartDate(value)}
                        state={errors.startDate.length > 0 ? InputStates.error : undefined}
                        disabled={isLoadingMyCouponDetail || wasUsed}
                        errorMessages={errors.startDate}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
                {/* end date */}
                <div className="col-md-6 col-12">
                    <Input
                        type={InputTypes.date}
                        label={`${translations.endDateLabel}*`}
                        variant={InputVariants.labeledInput}
                        value={endDate}
                        onChange={(value) => setEndDate(value)}
                        state={errors.endDate.length > 0 ? InputStates.error : undefined}
                        disabled={isLoadingMyCouponDetail || wasUsed}
                        errorMessages={errors.endDate}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
                {/* discount type */}
                <div className="col-md-6 col-12">
                    <div>
                        <Typography color="muted" fontSize="12">
                            <>{translations.discountTypeLabel}*</>
                        </Typography>
                        <Select
                            classes="w-100"
                            type={SelectType.variant1}
                            disabled={isLoadingMyCouponDetail || wasUsed}
                            options={[
                                { value: '', text: translations.default },
                                { value: 'fixed', text: translations.discountTypeFixed },
                                { value: 'percentage', text: translations.discountTypePercentage },
                            ]}
                            value={discountType || undefined}
                            onChange={(value) => setDiscountType(value)}
                        />
                        {errors.discountType.length > 0 && <ErrorMessages messages={errors.discountType} />}
                    </div>
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
                {/* discount value */}
                <div className="col-md-6 col-12 pt-md-2 mt-md-1">
                    <Input
                        type={InputTypes.number}
                        variant={InputVariants.labeledInput}
                        label={`${translations.discountValueLabel}*`}
                        value={`${discountValue}`}
                        onChange={(value) => setDiscountValue(value)}
                        disabled={isLoadingMyCouponDetail || wasUsed}
                        state={errors.discountValue.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.discountValue}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
                {/* total limit */}
                <div className="col-md-6 col-12">
                    <Input
                        type={InputTypes.number}
                        variant={InputVariants.labeledInput}
                        label={`${translations.totalLimitLabel}*`}
                        value={`${totalLimit}`}
                        onChange={(value) => setTotalLimit(value)}
                        disabled={isLoadingMyCouponDetail || wasUsed}
                        state={errors.totalLimit.length > 0 ? InputStates.error : undefined}
                        errorMessages={errors.totalLimit}
                    />
                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                </div>
            </div>

            {/* submit */}
            <div className="col-12 text-end">
                <Button
                    icon="fa-solid fa-floppy-disk"
                    label={translations.submitButton}
                    mode={ButtonMode.primary}
                    disabled={isLoadingMyCouponDetail}
                    onClick={() => handleOnClickSaveCoupon()}
                />
            </div>
        </Form>
    );
};
